import * as React from "react";
import { useState, useMemo, useCallback } from "react";
import styled, { css } from "styled-components";
import moment from "moment";
import { isPermitted } from "util/PermissionsMixin";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import useCurrentUser from "hooks/useCurrentUser";
import BootstrapModalConfirm from "components/bootstrap/BootstrapModalConfirm";
import { Button } from "components/bootstrap";
import { DataWell } from "common/components";
import { MarkdownEditor, MarkdownPreview } from "components/common/MarkdownEditor";
import { useAddNote, useEditNote, useDeleteNote } from "security-app/hooks/useInvestigationsAPI";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import { Row, FormGroup } from "./styled.components";
const NewNoteForm = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;

    background-color: ${theme.colors.global.contentBackground};
  `
);
const StyledTitle = styled.h2`
  margin-bottom: 1rem;
`;
const NoteHeaderRow = styled(Row)`
  justify-content: space-between;
  margin-bottom: 5px;
  font-weight: bold;
  align-items: flex-start;

  & .note-meta {
    font-weight: normal;
    color: gray;
  }
`;
const NoteRow = styled(Row)`
  margin-top: 0.4rem;
`;
const NoteButton = styled(Button)`
  height: 22px;
  border-width: 1px !important;
  line-height: 1;
  font-size: 0.75rem;
`;
const NoteContent = styled.div`
  width: 100%;

  & .action-button {
    transition: opacity 0.2s ease-in-out;
    opacity: 0;
  }

  &:hover .action-button {
    opacity: 1;
  }
`;
const NewNoteHeader = ({ newNote, onAddNote }) => /* @__PURE__ */ React.createElement(NoteHeaderRow, { $fullWidth: true }, /* @__PURE__ */ React.createElement("span", null, "Add Note"), /* @__PURE__ */ React.createElement(NoteButton, { bsStyle: "primary", bsSize: "small", onClick: onAddNote, disabled: !newNote }, "Add"));
const NoteHeader = ({ note, editing, canManageNotes, onAction }) => /* @__PURE__ */ React.createElement(NoteHeaderRow, { $fullWidth: true }, /* @__PURE__ */ React.createElement("span", { className: "note-meta" }, note.created_by, " - ", moment(note.created_at).format("MM/DD/YYYY HH:mm:ss")), canManageNotes && /* @__PURE__ */ React.createElement(Row, { $gap: "0.4rem" }, editing ? /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(NoteButton, { bsStyle: "primary", bsSize: "small", onClick: () => onAction("save") }, "Save"), /* @__PURE__ */ React.createElement(NoteButton, { bsSize: "small", onClick: () => onAction("cancel") }, "Cancel")) : /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
  NoteButton,
  {
    className: "action-button",
    bsStyle: "primary",
    bsSize: "small",
    role: "button",
    onClick: () => onAction("edit")
  },
  "Edit"
), /* @__PURE__ */ React.createElement(NoteButton, { className: "action-button", bsStyle: "danger", bsSize: "small", onClick: () => onAction("delete") }, "Delete"))));
function Notes({ investigation }) {
  const { permissions } = useCurrentUser();
  const sendTelemetry = useSendTelemetry();
  const [newNote, setNewNote] = useState("");
  const [editNoteContent, setEditNoteContent] = useState("");
  const [editNoteId, setEditNoteId] = useState(null);
  const [deleteNoteId, setDeleteNoteId] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const canManageNotes = useMemo(
    () => !investigation.archived && isPermitted(permissions, "investigations:edit"),
    [investigation.archived, permissions]
  );
  const { addNote } = useAddNote();
  const { editNote } = useEditNote();
  const { deleteNote } = useDeleteNote();
  const onAddNote = async () => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.SECURITY_APP.INVESTIGATION_NOTE_ADDED, {
      app_pathname: "security",
      app_section: "investigation"
    });
    await addNote({ invId: investigation.id, note: newNote });
    setNewNote("");
  };
  const saveNoteChanges = async (noteId) => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.SECURITY_APP.INVESTIGATION_NOTE_SAVED, {
      app_pathname: "security",
      app_section: "investigation"
    });
    await editNote({ invId: investigation.id, noteId, note: editNoteContent });
    setEditNoteId(null);
    setEditNoteContent("");
  };
  const onDeleteNote = async () => {
    await deleteNote({ invId: investigation.id, noteId: deleteNoteId });
    setShowDeleteDialog(false);
    setDeleteNoteId(null);
    sendTelemetry(TELEMETRY_EVENT_TYPE.SECURITY_APP.INVESTIGATION_NOTE_DELETED, {
      app_pathname: "security",
      app_section: "investigation"
    });
  };
  const onCancelDelete = () => {
    setShowDeleteDialog(false);
    setDeleteNoteId(null);
  };
  const handleNoteAction = (note) => (action) => {
    switch (action) {
      case "edit":
        setEditNoteId(note.id);
        setEditNoteContent(note.content);
        break;
      case "cancel":
        setEditNoteId(null);
        setEditNoteContent("");
        break;
      case "save":
        saveNoteChanges(note.id);
        break;
      case "delete":
        setShowDeleteDialog(true);
        setDeleteNoteId(note.id);
        break;
      default:
        break;
    }
  };
  const isEditing = useCallback((noteId) => noteId === editNoteId, [editNoteId]);
  return /* @__PURE__ */ React.createElement(DataWell, { $color: "content" }, /* @__PURE__ */ React.createElement(NewNoteForm, null, /* @__PURE__ */ React.createElement(StyledTitle, null, "Notes"), canManageNotes && /* @__PURE__ */ React.createElement(Row, { style: { margin: "1rem 0" } }, /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(NewNoteHeader, { newNote, onAddNote }), /* @__PURE__ */ React.createElement(
    MarkdownEditor,
    {
      id: "new-note",
      height: 150,
      value: newNote,
      onChange: (newValue) => setNewNote(newValue)
    }
  )))), investigation.notes.length === 0 ? /* @__PURE__ */ React.createElement("div", { style: { color: "gray" } }, "There are no notes to list") : investigation.notes.map((note) => /* @__PURE__ */ React.createElement(NoteRow, { key: note.id }, /* @__PURE__ */ React.createElement(NoteContent, null, /* @__PURE__ */ React.createElement(
    NoteHeader,
    {
      note,
      editing: isEditing(note.id),
      canManageNotes,
      onAction: handleNoteAction(note)
    }
  ), /* @__PURE__ */ React.createElement(
    MarkdownPreview,
    {
      show: !isEditing(note.id),
      "data-testid": `note-${note.id}`,
      withFullView: true,
      height: 150,
      value: note.content
    }
  ), isEditing(note.id) && /* @__PURE__ */ React.createElement(
    MarkdownEditor,
    {
      id: `note-${note.id}`,
      height: 150,
      value: note.content,
      onChange: (newValue) => setEditNoteContent(newValue)
    }
  )))), /* @__PURE__ */ React.createElement(
    BootstrapModalConfirm,
    {
      showModal: showDeleteDialog,
      title: "Delete Note",
      onConfirm: onDeleteNote,
      onCancel: onCancelDelete
    },
    "Are you sure you want to delete this note?"
  ));
}
export default Notes;
